import { ReactNode } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { cn } from '@repo/ui/utils'
import { Typography } from '@repo/ui/typography'

interface TableStateLayoutProps {
  children: ReactNode
  className?: string
}

interface TableStateContentProps {
  title: string
  description?: string
  children?: ReactNode
}

interface TableStateIconProps {
  children: ReactNode
  className?: string
}

interface TableStateProps {
  title: string
  description?: string
  icon?: ReactNode
  lottieSrc?: string
  lottieStyle?: React.CSSProperties
  action?: ReactNode
  className?: string
}

function TableStateLayout({ children, className = '' }: TableStateLayoutProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16 p-8 gap-8 border border-grey-200 rounded-lg bg-middleground w-full',
        className
      )}
    >
      {children}
    </div>
  )
}

function TableStateIcon({ children, className }: TableStateIconProps) {
  return <div className={cn('flex justify-center items-center', className)}>{children}</div>
}

function TableStateContent({ title, description, children }: TableStateContentProps) {
  return (
    <div className="text-center space-y-2">
      <Typography variant="h1" size="md" weight="semiBold" className="text-grey-800">
        {title}
      </Typography>
      {description && (
        <Typography size="base" className="text-grey-400">
          {description}
        </Typography>
      )}
      {children}
    </div>
  )
}

export function TableState({
  title,
  description,
  icon,
  lottieSrc,
  lottieStyle = { width: '200px', height: '150px' },
  action,
  className,
}: TableStateProps) {
  return (
    <TableStateLayout className={className}>
      <TableStateIcon>
        {lottieSrc ? <DotLottieReact src={lottieSrc} autoplay loop style={lottieStyle} /> : icon}
      </TableStateIcon>

      <TableStateContent title={title} description={description} />

      {action}
    </TableStateLayout>
  )
}

// Export individual components for more granular usage
export { TableStateLayout, TableStateIcon, TableStateContent }
